import { useState, useEffect } from 'react';

export type MapRegion = 'africa' | 'world';

interface UseMapDataResult {
  data: any;
  loading: boolean;
  error: string | null;
}

const cache: Record<MapRegion, any> = {
  africa: null,
  world: null,
};

export function useMapData(region: MapRegion): UseMapDataResult {
  const [data, setData] = useState<any>(cache[region]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If data is already cached, use it
    if (cache[region]) {
      setData(cache[region]);
      setLoading(false);
      setError(null);
      return;
    }

    // Fetch data if not cached
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const fileName = region === 'africa' ? 'africa.geo.json' : 'world.geo.json';
        const response = await fetch(`/${fileName}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch ${region} map data: ${response.statusText}`);
        }

        const jsonData = await response.json();
        cache[region] = jsonData;
        setData(jsonData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error(`Error fetching ${region} map data:`, err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [region]);

  return { data, loading, error };
}
